'use client';

import { HeartIcon } from '@c/icons';
import type { SvgIconProps } from '@c/icons/types';
import WishlistAction from '@c/toasts/WishlistAction';
import type { ProductDocument } from 'models/product';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useRouter, usePathname } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { logEvent, getItem } from '@util/analytics';
import {
  addProductToFavorites,
  removeProductFromFavorites,
} from '@util/firestore/products';
import { trackUserInteraction } from '@util/firestore/recommendations';

export const FavoriteButton = ({
  product,
  initialCount = 0,
}: {
  product: ProductDocument;
  initialCount?: number;
}) => {
  const likedProps: SvgIconProps = {
    pathProps: {
      stroke: 'none',
      fill: '#FF4242',
      className: 'hover:none',
    },
  };
  const defaultProps: SvgIconProps = {
    pathProps: {
      stroke: 'currentColor',
      fill: 'none',
    },
  };
  const [isLiked, setLiked] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(initialCount);

  const { userDoc } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();

  useEffect(() => {
    if (userDoc) {
      const liked = userDoc.favorites?.includes(product.id);
      setLiked(!!liked);
    }
  }, [product.id, userDoc]);

  const props = isLiked ? likedProps : defaultProps;

  const onButtonClick = () => {
    const wasLiked = isLiked;
    setLiked(!isLiked);

    if (userDoc) {
      if (wasLiked) {
        removeProductFromFavorites(userDoc.uid, product.id);
        setFavoriteCount(favoriteCount - 1);
        trackUserInteraction({
          uid: userDoc.uid,
          pid: product.id,
          interaction: 'unfavorite',
        });
      } else {
        addProductToFavorites(userDoc.uid, product.id);
        setFavoriteCount(favoriteCount + 1);
        trackUserInteraction({
          uid: userDoc.uid,
          pid: product.id,
          interaction: 'favorite',
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['authUser'],
      });
      queryClient.invalidateQueries({
        queryKey: ['favorites', userDoc?.uid],
      });
      logEvent(
        'add_to_wishlist',
        {
          currency: 'USD',
          items: [getItem(product)],
        },
        userDoc?.uid
      );
      showToast(<WishlistAction removed={wasLiked} />);
    } else router.push(`/login?redirect=${encodeURI(pathname ?? '/')}`);
  };

  return (
    <motion.button
      type="button"
      aria-label="favorite"
      className="absolute z-10 h-16 w-16 rounded-full bg-brand-white p-3 transition-colors duration-150 ease-linear hover:bg-zinc-200"
      whileTap={{ scale: 0.85 }}
      onClick={(e) => {
        e.preventDefault();
        onButtonClick();
      }}
    >
      <HeartIcon className="h-full w-full" {...props} />
    </motion.button>
  );
};
